import React, {useEffect, useState} from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {securedFetchRequest} from '@bidmii/common/lib/util/FetchRequest';
import {useRecoilValue} from 'recoil';
import {userState} from '../recoil/user/userAtom';
import {IUserResponse} from '@bidmii/common/lib/server/IServerApi';
import {theme} from '../Theme';
import {makeStyles} from '@mui/styles';
import {CUSTOM_EVENT_NAMES, fireCustomEvent} from '../util/Analytics';
import useCategories from './useCategories';
import * as Sentry from '@sentry/react';

const useStyles = makeStyles(() => ({
  autocomplete: {
    '& .MuiChip-deleteIcon': {
      color: theme.palette.blue.main,
    },
    '& .MuiAutocomplete-inputRoot': {
      minHeight: 37,
      height: 37,
      backgroundColor: theme.palette.gray.main,
      borderRadius: 50,
      paddingTop: 0,
      paddingBottom: 0,
      marginBottom: -1,
      fontFamily: 'AeonikProReqular',
      fontSize: 13,
      border: '1px solid transparent',

      '&:before': {
        borderBottom: 'none',
      },
      '&:after': {
        borderBottom: 'none',
      },
      '&:hover': {
        border: `1px solid ${theme.palette.primary.light}`,

        '&:before': {
          borderBottom: 'none',
        },
        '&:after': {
          borderBottom: 'none',
        },
      },
    },
  },
}));

export default function useCategoryMultiSelect(props?: $TSFixMe) {
  const classes = useStyles();
  const categoryIds = props?.categoryIds;
  const categoryName = props?.categoryName;

  const user = useRecoilValue(userState);
  const [selectedCategoryPage, setSelectedCategoryPage] = useState(0);
  const categories = useCategories();
  const [selectedCategories, setSelectedCategories] = useState<any>([]);

  useEffect(() => {
    if (categories.length > 0 && categoryIds) {
      setSelectedCategories(
        categoryIds?.length > 0
          ? categories.filter((cat) =>
              categoryIds.includes((cat as $TSFixMe).id),
            )
          : [],
      );
    }
  }, [categories, categoryIds]);

  useEffect(() => {
    if (categories.length > 0 && categoryName) {
      setSelectedCategories(
        categoryName
          ? categories.filter(
              (cat) =>
                categoryName.toLowerCase() ===
                (cat as $TSFixMe).name.toLowerCase(),
            )
          : [],
      );
    }
  }, [categories, categoryName]);

  const getMyCategories = () => {
    const url = (user as IUserResponse)._links.categories.href.replace(
      'page=0',
      `page=${selectedCategoryPage}`,
    );
    securedFetchRequest(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((res) => {
        if (res._embedded) {
          setSelectedCategories([
            ...selectedCategories,
            ...res._embedded.models,
          ]);
        }
        if (res?.page && selectedCategoryPage < res.page.totalPages - 1) {
          setSelectedCategoryPage((currentPage) => currentPage + 1);
        }
      })
      .catch((err) => {
        process.env.NODE_ENV === 'development' && console.log(err);
        Sentry.withScope(function (scope) {
          scope.setTag(
            'section',
            'useCategoryMultiSelect.tsx getMyCategories()',
          );
          Sentry.captureException(err);
        });
      });
  };

  useEffect(() => {
    if (
      !selectedCategories.length &&
      user?.userType === 'BIDDER' &&
      (user as IUserResponse)?._links?.categories &&
      (window.location.pathname.includes('/my/profile/dashboard') ||
        window.location.pathname.includes('/new/user'))
    ) {
      getMyCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryPage, user?.initialised]);

  const filterCategoryOptions = (options: any[]): any[] =>
    options.filter(
      (opt) => !selectedCategories.map((cat: any) => cat.id).includes(opt.id),
    );

  const categoryMultiSelect = (
    <>
      {categories.length === 0 ? (
        <CircularProgress size={25} />
      ) : (
        <Autocomplete
          className={classes.autocomplete}
          multiple
          fullWidth
          disableCloseOnSelect
          value={selectedCategories}
          onChange={(e, newVal) => setSelectedCategories(newVal)}
          onFocus={() =>
            fireCustomEvent(CUSTOM_EVENT_NAMES.CATEGORIES_MULTI_SELECT)
          }
          id="category-multi-select"
          options={categories}
          getOptionLabel={(option) => (option as $TSFixMe).name}
          isOptionEqualToValue={(option: any) =>
            selectedCategories.find((cat: any) => cat.id === option.id)
          }
          filterOptions={
            window.location.pathname === '/my/profile/dashboard'
              ? filterCategoryOptions
              : undefined
          }
          filterSelectedOptions
          includeInputInList
          limitTags={props?.limitTags || -1}
          renderInput={(params) => (
            <>
              {!props?.noLabel && (
                <Typography
                  variant="subtitle2"
                  align="left"
                  style={{
                    fontSize: 12,
                    marginBottom: 2,
                    color: theme.palette.primary.light,
                  }}>
                  Categories
                </Typography>
              )}
              <TextField
                {...params}
                variant="filled"
                placeholder={
                  selectedCategories.length === 0 ? 'Search categories' : ''
                }
              />
            </>
          )}
          ChipProps={{
            style: {
              backgroundColor: theme.palette.blue.light,
              border: `1px solid ${theme.palette.blue.main}`,
              height: 25,
              fontSize: 12,
            },
          }}
        />
      )}
    </>
  );

  return [categoryMultiSelect, selectedCategories, setSelectedCategories];
}
